/**
 * NotifCenterScreen — in-app notification inbox (`/notif`).
 *
 * Pixel-faithful port of `NotifCenterScreen` in
 * `design/project/screens-features.jsx`, adapted to the real notification
 * schema (`kind` + `payload` + `read_at`).
 *
 * Sections:
 *   - 今日: items whose `created_at` falls on today's local date.
 *   - 更早: everything else.
 *
 * Unread items render with a 3px left accent bar and slightly bolder
 * text. Tapping a row:
 *   - kind=occ_conflict   → opens the ConflictSheet (10.4)
 *   - kind=recurring_generated → navigates to /recur (parent handles)
 *   - kind=sync_failed    → navigates to /settings/sync (parent handles)
 *   - kind=system         → dismiss-on-tap only
 *
 * In all cases the item is marked read via `notifications.markRead(id)` and
 * an `update_notification` op is enqueued to persist the read_at.
 */
import { useMemo, useState } from 'react';

import Icon, { type IconName } from '@/components/common/Icon';
import { useStore } from '@/state/store';
import {
  groupByTime,
} from '@/state/slices/notifications';
import type {
  NotificationKind,
  NotificationRow,
} from '@/lib/sheets/types';
import ConflictSheet, {
  type ConflictPayload,
} from './ConflictSheet';

export interface NotifCenterScreenProps {
  /** Optional override for `now` — used in tests. */
  now?: Date;
  /** Navigation hook for cross-screen jumps. */
  onNavigate?: (path: '#/recur' | '#/settings/sync') => void;
}

const KIND_ICON: Record<NotificationKind, IconName> = {
  occ_conflict: 'cloudOff',
  recurring_generated: 'repeat',
  sync_failed: 'cloudOff',
  system: 'bell',
};

const KIND_COLOR: Record<NotificationKind, string> = {
  occ_conflict: 'var(--warn)',
  recurring_generated: 'var(--cat-home)',
  sync_failed: 'var(--clay-500)',
  system: 'var(--mocha-600)',
};

const KIND_TITLE: Record<NotificationKind, string> = {
  occ_conflict: '紀錄發生衝突',
  recurring_generated: '固定週期已產生',
  sync_failed: '同步失敗',
  system: '系統訊息',
};

function genOpId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatDesc(n: NotificationRow): string {
  try {
    const p = JSON.parse(n.payload || '{}') as Record<string, unknown>;
    if (n.kind === 'recurring_generated') {
      const c = typeof p.generated_count === 'number' ? p.generated_count : 0;
      return `自動新增 ${c} 筆交易`;
    }
    if (n.kind === 'sync_failed') {
      const op =
        typeof p.op === 'string' ? p.op : '未知操作';
      const err =
        typeof p.error === 'string'
          ? maskAuthSecrets(p.error)
          : '網路或 API 錯誤';
      return `${op} · ${err}`;
    }
    if (n.kind === 'occ_conflict') {
      const tx = typeof p.tx_id === 'string' ? p.tx_id : '一筆紀錄';
      return `${tx} 在另一台裝置已被修改`;
    }
    return n.payload || '';
  } catch {
    return '';
  }
}

/**
 * Mask any obvious bearer / API secrets in the displayed error text. We
 * keep the surrounding context so the user sees what failed without
 * leaking auth material into the UI (sharp edge per audit).
 */
function maskAuthSecrets(s: string): string {
  return s
    .replace(/(authorization\s*[:=]\s*)bearer\s+\S+/gi, '$1bearer ***')
    .replace(/(Bearer\s+)[A-Za-z0-9._-]+/g, '$1***')
    .replace(/(api[_-]?key\s*[:=]\s*)\S+/gi, '$1***')
    .replace(/(token\s*[:=]\s*)\S+/gi, '$1***');
}

export default function NotifCenterScreen({
  now: nowProp,
  onNavigate,
}: NotifCenterScreenProps): JSX.Element {
  const now = nowProp ?? new Date();
  const items = useStore((s) => s.notifications.items);
  const markRead = useStore((s) => s.notifications.markRead);
  const enqueue = useStore((s) => s.sync.enqueue);

  const [conflict, setConflict] = useState<{
    notifId: string;
    payload: ConflictPayload;
  } | null>(null);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.created_at === b.created_at) return 0;
      return a.created_at < b.created_at ? 1 : -1;
    });
  }, [items]);

  const grouped = useMemo(() => groupByTime(sorted, now), [sorted, now]);

  const unreadCount = items.filter((n) => !n.read_at).length;

  const onTap = (n: NotificationRow): void => {
    if (!n.read_at) {
      markRead(n.id);
      const ts = new Date().toISOString();
      // Bug fix: previously this enqueued `type: 'create_notification'`
      // with an `update_notification` payload. parseSyncOp would reject
      // it on the next reload (extra `id`/`read_at` keys not allowed for
      // the create_notification variant) and the read_at would silently
      // never persist. Use the correct `update_notification` op type.
      enqueue({
        id: genOpId(),
        type: 'update_notification',
        payload: { id: n.id, read_at: ts },
        attempts: 0,
        last_error: '',
        created_at: ts,
      });
    }

    if (n.kind === 'occ_conflict') {
      try {
        const p = JSON.parse(n.payload || '{}') as ConflictPayload;
        setConflict({ notifId: n.id, payload: p });
      } catch {
        /* malformed payload — ignore */
      }
      return;
    }
    if (n.kind === 'recurring_generated') {
      onNavigate?.('#/recur');
      return;
    }
    if (n.kind === 'sync_failed') {
      onNavigate?.('#/settings/sync');
      return;
    }
  };

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        background: 'var(--bg-app)',
        paddingBottom: 80,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '60px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1
          style={{
            font: '600 26px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          通知中心
        </h1>
        {unreadCount > 0 && (
          <span
            style={{
              font: '500 12px var(--font-cjk)',
              color: 'var(--fg-3)',
            }}
          >
            {unreadCount} 則未讀
          </span>
        )}
      </div>

      {/* Body */}
      {items.length === 0 ? (
        <div
          style={{
            padding: '60px 20px',
            textAlign: 'center',
            font: '500 14px var(--font-cjk)',
            color: 'var(--fg-3)',
          }}
        >
          目前沒有通知
        </div>
      ) : (
        <>
          {grouped.today.length > 0 && (
            <Section title="今日">
              {grouped.today.map((n, i) => (
                <NotifRow
                  key={n.id}
                  row={n}
                  isLast={i === grouped.today.length - 1}
                  onTap={() => onTap(n)}
                />
              ))}
            </Section>
          )}
          {grouped.earlier.length > 0 && (
            <Section title="更早">
              {grouped.earlier.map((n, i) => (
                <NotifRow
                  key={n.id}
                  row={n}
                  isLast={i === grouped.earlier.length - 1}
                  onTap={() => onTap(n)}
                />
              ))}
            </Section>
          )}
        </>
      )}

      {conflict && (
        <ConflictSheet
          notifId={conflict.notifId}
          payload={conflict.payload}
          onClose={() => setConflict(null)}
        />
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ padding: '14px 16px 0' }}>
      <div
        style={{
          font: '600 12px var(--font-cjk)',
          color: 'var(--fg-2)',
          letterSpacing: '0.06em',
          padding: '0 4px 8px',
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-1)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function NotifRow({
  row,
  isLast,
  onTap,
}: {
  row: NotificationRow;
  isLast: boolean;
  onTap: () => void;
}): JSX.Element {
  const unread = !row.read_at;
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 14px 14px 17px',
        background: unread ? 'rgba(199,154,75,0.06)' : 'transparent',
        border: 0,
        cursor: 'pointer',
        borderBottom: isLast ? 0 : '0.5px solid var(--border-1)',
        textAlign: 'left',
      }}
    >
      {unread && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: 'var(--accent)',
          }}
        />
      )}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: KIND_COLOR[row.kind],
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <Icon name={KIND_ICON[row.kind]} size={18} stroke="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: unread ? '600 14px var(--font-cjk)' : '500 14px var(--font-cjk)',
            color: 'var(--fg-1)',
          }}
        >
          {KIND_TITLE[row.kind]}
        </div>
        <div
          style={{
            font: '400 12.5px var(--font-cjk)',
            color: 'var(--fg-2)',
            marginTop: 3,
            lineHeight: 1.45,
            wordBreak: 'break-word',
          }}
        >
          {formatDesc(row)}
        </div>
        {row.created_at && (
          <div
            style={{
              font: '400 11px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 5,
            }}
          >
            {formatRelative(row.created_at)}
          </div>
        )}
      </div>
    </button>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
